import { useEffect, useMemo, useRef } from 'react';
import type { FormLayoutNode } from '../types/layout';
import type { FormSchema } from '../types/schema';
import { collectDocumentListStoreKeysFromLayout } from './collectDocumentListStoreKeys';
import {
  beginComposeFormSession,
  deriveFormSessionKey,
  endComposeFormSession,
  resolveFormSchemaId,
} from './composeFormSession';

/**
 * Returns a stable (same reference) sorted-and-joined fingerprint of the store-key array.
 * Used as the memo dependency so `beginComposeFormSession` is NOT re-fired on every render
 * just because the layout array is a new reference with the same keys.
 */
function useStableStoreKeys(layout: FormLayoutNode[]): string[] {
  const keys = collectDocumentListStoreKeysFromLayout(layout);
  const fingerprint = [...keys].sort().join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => keys, [fingerprint]);
}

export interface UseComposeFormSessionArgs {
  schema: FormSchema;
  layout: FormLayoutNode[];
  mode: string;
  params?: Record<string, string>;
  meta?: Record<string, unknown>;
}

export function useComposeFormSession({
  schema,
  layout,
  mode,
  params,
  meta,
}: UseComposeFormSessionArgs): string {
  const formSessionKey = useMemo(
    () =>
      typeof meta?.formSessionKey === 'string' && meta.formSessionKey.length > 0
        ? meta.formSessionKey
        : deriveFormSessionKey(mode, params),
    [meta?.formSessionKey, mode, params],
  );

  // Stable reference — only changes when the actual store keys change, not on every layout re-render.
  const documentListStoreKeys = useStableStoreKeys(layout);

  const formSchemaId = useMemo(
    () => resolveFormSchemaId(meta, schema.features?.namespace),
    [meta, schema.features?.namespace],
  );

  const sessionArgsRef = useRef({
    formSessionKey,
    mode,
    documentListStoreKeys,
    formSchemaId,
  });
  sessionArgsRef.current = {
    formSessionKey,
    mode,
    documentListStoreKeys,
    formSchemaId,
  };

  // Re-bind active session when mode/key changes (e.g. in-place view → edit) without clearing buckets.
  useEffect(() => {
    beginComposeFormSession({
      formSessionKey,
      mode,
      documentListStoreKeys,
      formSchemaId,
    });
  }, [formSessionKey, mode, formSchemaId, documentListStoreKeys]);

  // Tear down only when the form block unmounts (modal close), not on mode switches.
  useEffect(() => {
    return () => {
      endComposeFormSession({
        ...sessionArgsRef.current,
        reason: 'exit',
      });
    };
  }, []);

  return formSessionKey;
}
